import React from 'react';
import PropTypes from 'prop-types';
import { Box, Avatar, Typography } from '@mui/material';
import { prepareRoleColorForChat } from '../../utils/roleColorValidation';
import ActivityGlyph from './ActivityGlyph';

/**
 * TypingIndicator — indicador de actividad del agente (camino SIN streaming).
 *
 * Ojo con el reparto: este componente sirve el **98 % del tráfico** de producción,
 * y hasta #923 estaba por detrás de `StreamingToolLoader` (el del 2 %) en cinco
 * cosas: no truncaba, no tenía `role="status"` ni `aria-live`, ignoraba
 * `prefers-reduced-motion` y no podía mostrar el icono de la herramienta. Esa
 * asimetría es la que hizo que la UI buena la viera casi nadie.
 *
 * Lo que se pinta, de izquierda a derecha:
 *
 *   [avatar]  Copiloto
 *             ✻ 📊 Ejecutando código · sumando primos…            12s
 *
 * El glifo animado **sustituye a los tres puntos**: los dos juntos son redundantes y
 * el glifo deja el hueco para el icono de la herramienta. El cronómetro va a la
 * derecha, en cifras de ancho fijo para que no baile.
 *
 * Presentación pura — sin temporizadores. El reloj lo lleva `useElapsedTime` en el
 * wrapper, que es quien sabe cuándo se enciende y se apaga el indicador.
 *
 * @param {string} [agentName] - Nombre del agente. Sin él se usa el diseño mínimo.
 * @param {string} [task] - Estado actual. Se trunca visualmente: puede llegar un
 *   párrafo (medido en prod: 5,4 % de los estados pasan de 80 chars, máximo 235).
 * @param {string} [elapsedLabel] - Tiempo transcurrido ya formateado (`12s`, `1:45`).
 *   `null`/ausente mientras no merezca la pena mostrarlo.
 * @param {string} [toolIcon] - Icono de la herramienta en curso (de `toolLabels.js`).
 * @param {string} [avatarColor] - Color del avatar del rol.
 * @param {string} [initials] - Iniciales del avatar.
 * @param {Object} [theme] - Tema MUI, para el cálculo de contraste WCAG AA.
 */
const TypingIndicator = ({ agentName, task, elapsedLabel, toolIcon, avatarColor, initials, theme }) => {
  const hasAgent = Boolean(agentName);

  // Contraste adecuado (WCAG AA) — nunca hex a pelo: `docs/architecture/chat/CHAT_COLOR_TOKENS.md`.
  const avatarColors = avatarColor && theme
    ? prepareRoleColorForChat(avatarColor, theme)
    : null;

  const glyphColor = theme?.chat?.typing?.dotColor;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: 'flex',
        gap: { xs: 0.5, md: 1 },
        alignItems: 'flex-start',
        animation: 'fadeIn 0.3s ease-in',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    >
      {/* Avatar del agente — sólo si lo conocemos */}
      {hasAgent && (
        <Avatar sx={{
          ...(avatarColors
            ? { background: avatarColors.avatarBgGradient, boxShadow: avatarColors.avatarShadow }
            : { bgcolor: 'primary.main' }),
          color: avatarColors ? avatarColors.avatarText : 'primary.contrastText',
          width: { xs: 24, md: 32 },
          height: { xs: 24, md: 32 },
          fontSize: { xs: '0.75rem', md: '0.875rem' },
          fontWeight: 600,
          flexShrink: 0,
        }}>
          {initials}
        </Avatar>
      )}

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        bgcolor: 'background.paper',
        borderRadius: 2,
        px: { xs: 1, md: 1.5 },
        py: { xs: 1, md: 1.25 },
        border: '1px solid',
        borderColor: 'divider',
        minWidth: { xs: 100, md: 120 },
        // Sin este tope, un estado largo estira la burbuja y empuja el layout.
        maxWidth: '100%',
      }}>
        {hasAgent && (
          <Typography variant="caption" sx={{
            color: 'text.primary',
            fontWeight: 600,
            fontSize: { xs: '0.7rem', md: '0.75rem' },
          }}>
            {agentName}
          </Typography>
        )}

        {/* Línea de estado: glifo · [icono tool] · texto · cronómetro */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.75, md: 1 },
          minWidth: 0,
        }}>
          <ActivityGlyph size={14} color={glyphColor} />

          {toolIcon && (
            <Box component="span" aria-hidden="true" sx={{ fontSize: '1.05em', flexShrink: 0, lineHeight: 1 }}>
              {toolIcon}
            </Box>
          )}

          {task && (
            <Typography
              variant="body2"
              key={task}
              title={task}
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                // El truncado que faltaba: un estado de 235 chars ya no rompe la fila.
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                animation: 'taskFadeIn 0.3s ease',
                '@keyframes taskFadeIn': {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }}
            >
              {task}
            </Typography>
          )}

          {elapsedLabel && (
            <Typography
              component="span"
              variant="caption"
              // Fuera del anuncio: este contenedor es `aria-live`, y un contador que
              // cambia cada segundo haría que el lector de pantalla recitara «12
              // segundos», «13 segundos»… sin parar. El tiempo es una señal visual;
              // lo que se anuncia es el texto del estado.
              aria-hidden="true"
              sx={{
                color: 'text.disabled',
                flexShrink: 0,
                // Cifras de ancho fijo: si no, el número baila al pasar de 9s a 10s.
                fontVariantNumeric: 'tabular-nums',
                fontSize: { xs: '0.7rem', md: '0.75rem' },
              }}
            >
              {elapsedLabel}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

TypingIndicator.propTypes = {
  agentName: PropTypes.string,
  task: PropTypes.string,
  elapsedLabel: PropTypes.string,
  toolIcon: PropTypes.string,
  avatarColor: PropTypes.string,
  initials: PropTypes.string,
  theme: PropTypes.object,
};

export default TypingIndicator;
